import { IAttachment } from '~/client/graph/types.generated'
import type { IIconCatalog } from '~/client/src/shared/components/IconPicker'
import IHierarchyParent from '~/client/src/shared/types/IHierarchyParent'

export enum FieldType {
  TEXT = 'text',
  TEXTAREA = 'textarea',
  NUMBER = 'number',
  SELECT = 'select',
  HIERARCHY = 'hierarchy',
  DATETIME = 'datetime',
  CHECKBOX = 'checkbox',
  USER = 'user',
  COMPANY = 'company',
  FILE = 'file',
  LOCATION = 'location',
  COLOR = 'color',
  ICON = 'icon',
  PARENT = 'parent',
}

export type FileAttachment = IAttachment
export type FileUploadFn = (file: File) => Promise<FileAttachment>

interface BaseFieldConfig {
  name: string
  type: FieldType
  label?: string
  /** Explains the label behind an info icon beside it. Honoured by PARENT so far. */
  labelTooltip?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  /**
   * Keeps the field in place with its normal look but can't be changed; unlike
   * `readOnly` it stays in the form, so its value is still submitted.
   */
  displayOnly?: boolean
  /**
   * Always render this field (and never offer it in "Add field"), independent of
   * `required` — for optional-but-permanent fields like the equipment map
   * properties (color/icon/parent). `required` still drives the Save gate.
   */
  alwaysVisible?: boolean
  /**
   * Consecutive fields sharing the same non-empty `rowGroup` render side by side
   * in one row (e.g. icon + color), instead of stacked.
   */
  rowGroup?: string
}

export interface TextFieldConfig extends BaseFieldConfig {
  type: FieldType.TEXT
  placeholder?: string
  defaultValue?: string
}

export interface TextAreaFieldConfig extends BaseFieldConfig {
  type: FieldType.TEXTAREA
  placeholder?: string
  defaultValue?: string
  maxLength?: number
  rows?: number
}

export interface NumberFieldConfig extends BaseFieldConfig {
  type: FieldType.NUMBER
  placeholder?: string
  defaultValue?: number
}

export interface SelectOption {
  value: string | number | null
  label: string
  deleted?: boolean
  isNew?: boolean
}

export interface SelectFieldConfig extends BaseFieldConfig {
  type: FieldType.SELECT
  options: SelectOption[]
  placeholder?: string
  defaultValue?: string | number | null | (string | number | null)[]
  multiSelect?: boolean
  allowUserCreation?: boolean
  includeNoneOption?: boolean
}

export interface HierarchyFormOption extends SelectOption {
  value: string
  parentValue?: string
}

export interface HierarchyFieldConfig extends BaseFieldConfig {
  type: FieldType.HIERARCHY
  options: HierarchyFormOption[]
  placeholder?: string
  defaultValue?: string | null
}

export interface DateTimeFieldConfig extends BaseFieldConfig {
  type: FieldType.DATETIME
  placeholder?: string
  defaultValue?: string | number | null
}

export interface FileFieldConfig extends BaseFieldConfig {
  type: FieldType.FILE
  defaultValue?: FileAttachment[]
  maxFiles?: number
  acceptedFileTypes?: string[]
  maxFileSizeMb?: number
  placeholder?: string
}

export interface CheckboxFieldConfig extends BaseFieldConfig {
  type: FieldType.CHECKBOX
  defaultValue?: boolean
}

export interface UserFieldConfig extends BaseFieldConfig {
  type: FieldType.USER
  defaultValue?: string
}

export interface CompanyFieldConfig extends BaseFieldConfig {
  type: FieldType.COMPANY
  defaultValue?: string
}

export interface LocationFieldConfig extends BaseFieldConfig {
  type: FieldType.LOCATION
  defaultValue?: string
}

export interface ColorFieldConfig extends BaseFieldConfig {
  type: FieldType.COLOR
  defaultValue?: string | null
  /** The icon field this colour paints. A library icon carries its own colours,
   *  so the field explains what the colour still reaches while that one holds a pictogram. */
  iconFieldName: string
}

export interface IconFieldConfig extends BaseFieldConfig {
  type: FieldType.ICON
  defaultValue?: string | null
  /** The icons this field offers. The renderer has no business knowing which
   *  resource type it is editing, so the set travels with the config. */
  catalog: IIconCatalog
  /** The field holding the location's type, whose own icons the picker lists first. */
  kindFieldName?: string
}

export interface ParentFieldConfig extends BaseFieldConfig {
  type: FieldType.PARENT
  defaultValue?: IHierarchyParent | null
  /** Excluded, with its descendants, from the parent choices. */
  objectId?: string | null
}

export type FieldConfig =
  | TextFieldConfig
  | TextAreaFieldConfig
  | NumberFieldConfig
  | SelectFieldConfig
  | HierarchyFieldConfig
  | DateTimeFieldConfig
  | FileFieldConfig
  | CheckboxFieldConfig
  | UserFieldConfig
  | CompanyFieldConfig
  | LocationFieldConfig
  | ColorFieldConfig
  | IconFieldConfig
  | ParentFieldConfig

export const DYNAMIC_FORM_ID = 'dynamic-form'

export interface DynamicFormProps {
  fields: FieldConfig[]
  onSubmit: (data: Record<string, unknown>) => void
  fileUploadFn?: FileUploadFn
  className?: string
  submitButtonText?: string
  isSubmitting?: boolean
  hideSubmitButton?: boolean
  hideHelperText?: boolean
  formId?: string
  /**
   * Skip native HTML constraint validation and rely on react-hook-form's rules
   * instead. Needed when required fields may be inside a collapsed
   * (`display:none`) section, where native validation throws
   * "invalid form control is not focusable" and silently blocks submit.
   */
  noValidate?: boolean
  onFieldsChange?: (
    fieldName: string,
    updatedField: Partial<FieldConfig>,
  ) => void
}
